import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Alert,
    Typography,
    Divider,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Tooltip,
    styled
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ReactMarkdown from 'react-markdown';
import useChannelForm from '../hooks/useChannelForm';
import { API_BASE_URL } from '../../../../../config/api';
import LlmConfigSettings from './LlmConfigSettings';
import TextFieldWithModal from '../TextFieldWithModal';
import InstructionHistoryButton from '../../InstructionHistoryButton';
import useIsMobile from '../../../useIsMobile';
import WebKeyManagement from './WebKeyManagement';
import '../../AssistantSettingsTheme.css';

// Styled component for code blocks
const CodeBlock = styled('pre')(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#2d2d2d' : '#f5f5f5',
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'monospace',
    fontSize: '0.9rem',
    position: 'relative',
    color: theme.palette.text.primary,
}));

const WebChannelSettings = ({ initialSettings = {}, agentGreeting = '', agentId = null, assistantId = null, channelType = 'WEB', onSave, saving, error, success }) => {
  const { values, handleChange, hasChanges } = useChannelForm(initialSettings);
  const isMobile = useIsMobile();
  const resolvedAssistantId = assistantId || agentId;

  // --- State for Web Key Status ---
  const [webKeys, setWebKeys] = useState([]);
  const [isCopied, setIsCopied] = useState(false);

  const hasWebKeys = webKeys.some((k) => k.is_active);
  const checkingKeys = false; // WebKeyManagement owns its own loading state

  // --- State for fetching instructions ---
  const [instructions, setInstructions] = useState('');
  const [instructionsError, setInstructionsError] = useState('');
  const [isLoadingInstructions, setIsLoadingInstructions] = useState(true);


  // --- Fetch instructions markdown file ---
  useEffect(() => {
    const fetchInstructions = async () => {
        setIsLoadingInstructions(true);
        try {
            const response = await fetch(`/chat-embed-instructions.md`);
            if (!response.ok) {
                throw new Error(`Failed to load integration guide (status: ${response.status})`);
            }
            const text = await response.text();
            setInstructions(text);
        } catch (error) {
            setInstructionsError(error.message);
        } finally {
            setIsLoadingInstructions(false);
        }
    };

    fetchInstructions();
}, []);


  const handleSaveClick = () => {
    onSave('WEB', values);
  };

  const handleLlmConfigChange = (key, value) => {
    const newKey = key.replace('llm_config.', '');
    handleChange(newKey, value);
  };

  const llmConfigValues = {
    llm_config: {
      model: values.model,
    }
  };
  
  // Note: We cannot display the raw key here anymore for security.
  // We use a placeholder and instruct the user.
  const apiKeyPlaceholder = 'YOUR_WEB_KEY';
  const scriptUrl = process.env.REACT_APP_ENV === 'dev'
    ? 'https://gentle-grass-02d3f240f.1.azurestaticapps.net/assistant-chat-embed.js'
    : 'https://portal.nexopta.com/assistant-chat-embed.js';
  const Backend_url = process.env.REACT_APP_ENV === 'dev'
    ? 'https://salesopt-app.redriver-d84691b9.eastus.azurecontainerapps.io'
    : 'https://salesoptai-app-prod.icystone-9246cdc7.canadaeast.azurecontainerapps.io';

  const embedCode = `<div id="assistant-chat-embed"
  data-assistant-id="${resolvedAssistantId || 'YOUR_ASSISTANT_ID'}"
  data-api-key="${apiKeyPlaceholder}"
  data-api-base-url="${API_BASE_URL}"
  data-color="#000000"
  data-title="Chat with us">
</div>
<script src="${scriptUrl}"></script>`;

  const handleCopyCode = () => {
    navigator.clipboard.writeText(embedCode).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    });
  };

  const apiKeyNotice = 'Replace `YOUR_WEB_KEY` with a valid web key generated below.';

  return (
    <Box className="assistantSettingsRoot" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}
      
      <TextField
        label="Initial Greeting"
        value={values.placeholder || ''}
        onChange={(e) => handleChange('placeholder', e.target.value)}
        helperText={isMobile ? undefined : 'Hint text displayed inside the chat input box before the user types (not an assistant message).'}
        fullWidth
      />

      <TextFieldWithModal
        label="Instructions"
        value={values.instructions || ''}
        onChange={(e) => handleChange('instructions', e.target.value)}
        rows={10}
        headerAction={
          agentId ? (
            <InstructionHistoryButton
              agentId={agentId}
              channelType={channelType}
              onRestore={(value) => handleChange('instructions', value)}
            />
          ) : null
        }
      />

      <Accordion sx={{ mt: 1 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="advanced-settings-content"
          id="advanced-settings-header"
        >
          <Typography>Advanced Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
            <LlmConfigSettings
                values={llmConfigValues}
                onChange={handleLlmConfigChange}
                channel="web"
            />
        </AccordionDetails>
      </Accordion>

      <Button
        variant="contained"
        color="primary"
        onClick={handleSaveClick}
        disabled={!hasChanges || saving || success}
        className="assistantPrimaryButton assistantButtonAlignEnd"
        sx={{ mt: 2 }}
      >
        {saving ? 'Saving...' : isMobile ? 'Save' : 'Save Web Channel Settings'}
      </Button>

      <Divider sx={{ my: 2 }} />

      {/* --- Web Key Management --- */}
      {resolvedAssistantId && (
        <WebKeyManagement assistantId={resolvedAssistantId} onKeysChange={setWebKeys} />
      )}

      <Divider sx={{ my: 2 }} />

      {/* --- Embed Code Section --- */}
      <Box className="assistantSettingsPanel" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6">Embed Chat Widget</Typography>

        {/* Web Key Status Warning */}
        {!checkingKeys && !hasWebKeys && (
            <Alert severity="warning">
                You do not have any active web keys. Generate one above before integrating the widget.
            </Alert>
        )}
        
        {!checkingKeys && hasWebKeys && !isMobile && (
            <Alert severity="info">
                <strong>Important:</strong> Replace <code>YOUR_WEB_KEY</code> in the code below with one of your active web keys generated above.
            </Alert>
        )}

        {!isMobile && (
            <Typography variant="body2" color="text.secondary">
                Add the following code just before the closing <code>&lt;/body&gt;</code> tag of your website.
            </Typography>
        )}

        <Box sx={{ position: 'relative' }}>
            <CodeBlock sx={isMobile ? { fontSize: '0.72rem', p: 1.5, pr: 5, maxHeight: 220, overflow: 'auto' } : undefined}>
                <code>{embedCode}</code>
            </CodeBlock>
            <Tooltip title={isCopied ? 'Copied!' : 'Copy to Clipboard'}>
                <IconButton
                    onClick={handleCopyCode}
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                >
                    {isCopied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Tooltip>
        </Box>

        <Accordion sx={{ mt: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>View Integration Guide</Typography>
            </AccordionSummary>
            <AccordionDetails>
                {isLoadingInstructions ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography>Loading guide...</Typography>
                    </Box>
                ) : instructionsError ? (
                    <Alert severity="error">{instructionsError}</Alert>
                ) : (
                    <Box sx={{
                        '& h1, & h2, & h3, & h4, & h5, & h6': { typography: 'h6', mt: 2, mb: 1 },
                         '& p': { typography: 'body2', mb: 1 },
                        '& ul, & ol': { pl: 3, mb: 1 },
                        '& li': { typography: 'body2', mb: 0.5 },
                        '& code': {
                            backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                            padding: '0.2em 0.4em',
                            fontSize: '85%',
                            borderRadius: '6px',
                            fontFamily: 'monospace',
                        },
                        '& pre': {
                            p: 2,
                            borderRadius: 1,
                            overflowX: 'auto',
                            backgroundColor: theme => theme.palette.mode === 'dark' ? '#2d2d2d' : '#f5f5f5',
                            border: theme => `1px solid ${theme.palette.divider}`,
                        },
                    }}>
                        <ReactMarkdown>{instructions
                            .replace('{{apiKeyNotice}}', apiKeyNotice)
                            .replace(/YOUR_WEB_KEY/g, apiKeyPlaceholder)
                            .replace(/BACKEND_URL/g, Backend_url)
                            .replace(/EMBED_JS_URL/g, scriptUrl)}
                        </ReactMarkdown>
                    </Box>
                )}
            </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

export default WebChannelSettings;